import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import SendEmailDialog from '../../components/shared/dialogs/SendEmailDialog';
import SendWhatsappDialog from '../../components/shared/dialogs/SendWhatsappDialog';
import {
    Box,
    Text,
    Heading,
    Badge,
    Textarea,
    VStack,
    HStack,
    Grid,
    Flex,
    Icon,
    Image,
} from '@chakra-ui/react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {
    DialogRoot,
    DialogContent,
    DialogHeader,
    DialogBody,
    DialogTitle,
    DialogCloseTrigger,
} from '../../components/ui/dialog';
import { useColorModeValue } from '../../components/ui/color-mode';
import {
    FaEnvelope,
    FaEdit,
    FaCheckCircle,
    FaStickyNote,
    FaStar,
    FaBox,
    FaWhatsapp,
} from 'react-icons/fa';
import { MdNoteAdd, MdHistory, MdFeedback } from 'react-icons/md';
import FitTabs from '../../components/shared/tabs/Tabs';
import { setCustomerComplaintTab } from '../../store/customerSlice';
import SelectBox from '../../components/shared/fields/SelectBox';
import ButtonBox from '../../components/shared/buttons/ButtonBox';
import { CheckboxChakra } from '../../components/ui/checkbox';
import { loadComplaintStatusAndPriorityRequest } from '../../store/complaintStatusAndPrioritySlice';
import transformStatusOptions from './statusOptionHelper';
import { resetNoteState } from '../../store/complaintSlice';
import ComplaintDetailsWithImages from './ComplaintDetailsWithImages';
import { LuStar, LuStarOff } from 'react-icons/lu';
import { uploadPath } from '../../utils/constants/insightsPath';
import { clearImageChanges } from '../../store/filesSlice';
import { showAlert } from '../../store/alertSlice';
import { clearComplaintProductData } from '../../store/productSlice';
import ImagePreviewDialog from '../../components/shared/dialogs/ImagePreviewDialog';
import { SimpleGrid } from '@chakra-ui/react';

const NoteDialog = ({ isOpen, onClose, initialValue = '' }) => {
    const [note, setNote] = useState(initialValue);
    const [error, setError] = useState('');
    const dispatch = useDispatch();

    const { customerId, complaintId, noteLoading, noteSuccess } = useSelector(state => state.complaint);

    const bgColor = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.600');
    const textColor = useColorModeValue('gray.700', 'gray.200');

    useEffect(() => {
        if (isOpen) {
            setNote(initialValue);
            dispatch(resetNoteState());
        }
    }, [isOpen, initialValue, dispatch]);

    useEffect(() => {
        if (noteSuccess) {
            setNote('');
            onClose();
            dispatch(resetNoteState());
        }
    }, [noteSuccess, onClose, dispatch]);

    const handleAddNote = () => {
        if (note.trim().length < 10) {
            setError('At least 10 characters are required');
            return;
        }
        setError('');
        dispatch({
            type: "ADD_NOTE",
            payload: {
                complaintId: complaintId,
                customerId: customerId,
                description: note.trim()
            }
        });
    };

    const handleClose = () => {
        setNote('');
        dispatch(resetNoteState());
        onClose();
    };

    return (
        <DialogRoot open={isOpen} onOpenChange={({ open }) => !open && handleClose()}>
            <DialogContent
                maxWidth="sm"
                bg={bgColor}
                borderRadius="lg"
                placement="center"
                p={6}
            >
                {/* Header */}
                <Flex justify="space-between" align="start" mb={4}>
                    <HStack spacing={3}>
                        <Icon as={FaStickyNote} color="blue.500" boxSize={5} />
                        <Text fontSize="lg" fontWeight="bold" color={textColor}>
                            Add Note
                        </Text>
                    </HStack>
                    <DialogCloseTrigger />
                </Flex>

                {/* Body */}
                <VStack align="stretch" spacing={4}>
                    <Box>
                        <Text fontSize="sm" fontWeight="medium" mb={1} color="gray.600">
                            Note
                        </Text>
                        <Textarea
                            placeholder="Enter your note here..."
                            value={note}
                            onChange={(e) => {
                                setNote(e.target.value);
                                if (error && e.target.value.trim()) {
                                    setError('');
                                }
                            }}
                            minHeight="100px"
                            borderRadius="md"
                            border="1px solid"
                            borderColor="gray.300"
                            _focus={{
                                borderColor: 'blue.400',
                                boxShadow: '0 0 0 1px blue.400',
                            }}
                            resize="vertical"
                            disabled={noteLoading}
                        />
                        {error && (
                            <Text fontSize="sm" color="red.500" mt={1}>
                                {error}
                            </Text>
                        )}
                    </Box>
                </VStack>

                {/* Footer */}
                <Flex mt={6} pt={4} borderTop="1px solid" borderColor={borderColor} justify="flex-end">
                    <HStack spacing={3}>
                        <ButtonBox
                            size="sm"
                            name="Cancel"
                            onClick={handleClose}
                            disabled={noteLoading}
                        />
                        <ButtonBox
                            size="sm"
                            name={noteLoading ? "Adding..." : "Add"}
                            variant="solid"
                            colorPalette="blue"
                            onClick={handleAddNote}
                            loading={noteLoading}
                        />
                    </HStack>
                </Flex>
            </DialogContent>
        </DialogRoot>
    );
};

const ComplaintDetailsDialog = ({ isOpen, onClose }) => {
    const dispatch = useDispatch();
    const complaintData = useSelector((state) => state.complaint);
    const { complaintProductData, complaintProductLoading, complaintProductError } = useSelector(
        (state) => state.product
    );

    const showWhatsappFeature = String(process.env.REACT_APP_WHATSAPP_SEND_MESSAGE_FEATURE ?? '').toLowerCase() === 'true';
    const activeTab = useSelector((state) => state.customer.activeComplaintTab);

    // useEffect(() => {
    //     console.log('REACT_APP_WHATSAPP_SEND_MESSAGE_FEATURE =', process.env.REACT_APP_WHATSAPP_SEND_MESSAGE_FEATURE, '-> showWhatsappFeature =', showWhatsappFeature);
    // }, []);

    const handleTabChange = (newTab) => {
        dispatch(setCustomerComplaintTab(newTab.value));
    };

    // Fetch complaint product data when View Product tab is opened
    useEffect(() => {
        if (activeTab === 'product' && complaintData?.productId && !complaintProductData) {
            dispatch({
                type: 'FETCH_COMPLAINT_PRODUCT_DATA',
                payload: { productId: complaintData.productId },
            });
        }
    }, [activeTab, complaintData?.productId, complaintProductData, dispatch]);

    // Clear complaint product data when dialog is closed
    useEffect(() => {
        if (!isOpen) {
            dispatch(clearComplaintProductData());
        }
    }, [isOpen, dispatch]);

    const [isEditMode, setIsEditMode] = useState(false);
    const [resolvedError, setResolvedError] = useState('');
    const [showNoteDialog, setShowNoteDialog] = useState(false);
    const [showEmailDialog, setShowEmailDialog] = useState(false);
    const [isWhatsappOpen, setIsWhatsappOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [showResolvedDialog, setShowResolvedDialog] = useState(false);
    const [, setAssignedTo] = useState('');
    const [resolvedRemarks, setResolvedRemarks] = useState('');
    const [requestFeedback, setRequestFeedback] = useState(true);
    const [selectedProductImage, setSelectedProductImage] = useState(null);
    const [isProductImageDialogOpen, setIsProductImageDialogOpen] = useState(false);

    const { statusOptions: apiStatusOptions } = useSelector((state) => state.statusAndPriority);
    const statusOptions = transformStatusOptions(apiStatusOptions);

    const schema = z.object({
        title: z.string().min(1, "Title is required"),
        description: z.string().min(1, "Description is required"),
        serialNo: z.string().optional(),
        complaintReferenceNo: z.string().optional(),
        complaintType: z.array(z.string()).min(1, "Complaint Type is required"),
        complaintSubType: z.array(z.string()).min(1, "Complaint Sub Type is required"),
        status: z.array(z.string()).nonempty("Status is required"),
    });


    const { register, handleSubmit, control, formState: { errors }, reset, setValue } = useForm({
        resolver: zodResolver(schema),
        defaultValues: {
            title: '',
            description: '',
            serialNo: '',
            complaintReferenceNo: '',
            complaintType: [],
            complaintSubType: [],
            status: []
        }
    });

    const selectedComplaintType = useWatch({
        control,
        name: 'complaintType',
    });



    const rawWatched = useWatch({ control, name: "status" });
    const watchedStatus = Array.isArray(rawWatched) && rawWatched.length > 0 ? rawWatched[0] : null;

    const lightBgColor = useColorModeValue('blue.50', 'blue.900');
    useEffect(() => {
        if (isOpen && complaintData) {
            const matchingOption = statusOptions.find(option =>
                option.value === complaintData.rawStatus.toLowerCase().replace(/[\s_]+/g, '')
            );

            const statusValue = matchingOption ? [matchingOption.value] : [];

            setValue("status", statusValue);
            setAssignedTo(complaintData.assignedTo || "");
            setResolvedRemarks("");
            // setRequestFeedback(false);
        }
    }, [isOpen, complaintData, reset, setValue]);

    const bgColor = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.600');
    const sidebarBg = useColorModeValue('gray.50', 'gray.700');
    const textColor = useColorModeValue('gray.700', 'gray.200');
    const headingColor = useColorModeValue('gray.800', 'white');
    const mutedColor = useColorModeValue('gray.500', 'gray.400');
    const cardBg = useColorModeValue('white', 'gray.750');
    const productDetailsBg = useColorModeValue('blue.50', 'blue.950');
    const productImagesBg = useColorModeValue('purple.50', 'purple.950');

    const { serviceRequestCategories, serviceRequestSubcategories } = useSelector((state) => state.categories || {
        serviceRequestCategories: [],
        serviceRequestSubcategories: {},
    });
    useEffect(() => {
        if (
            isOpen &&
            complaintData &&
            selectedComplaintType &&
            serviceRequestSubcategories[selectedComplaintType[0]]?.length > 0
        ) {
            const defaultSubCategory = complaintData.subCategoryObject?.value;
            if (defaultSubCategory) {
                setValue('complaintSubType', [defaultSubCategory]);
            }
        }
    }, [isOpen, complaintData, selectedComplaintType, serviceRequestSubcategories, setValue]);


    useEffect(() => {
        dispatch(loadComplaintStatusAndPriorityRequest());
    }, []);

    useEffect(() => {
        if (isOpen && complaintData) {

            const rawStatus = complaintData.rawStatus.toLowerCase().replace(/[\s_]+/g, '');
            let matchingOption = null;

            if (rawStatus) {
                matchingOption = statusOptions.find(option =>
                    option.value === rawStatus.toLowerCase()
                );
            }

            const formData = {
                title: complaintData.title || '',
                description: complaintData.description || '',
                serialNo: complaintData.serialNo || '',
                complaintReferenceNo: complaintData.complaintReferenceNumber || '',
                complaintType: complaintData.categoryObject?.value
                    ? [complaintData.categoryObject.value]
                    : [],
                complaintSubType: complaintData.subCategoryObject?.value
                    ? [complaintData.subCategoryObject.value]
                    : [],
                status: matchingOption ? [matchingOption.value] : [],
            };

            reset(formData);

            Object.keys(formData).forEach(key => {
                setValue(key, formData[key]);
            });
        }
    }, [complaintData, isOpen, reset, setValue]);

    const onSubmit = async (data, imageChanges) => {
        // console.log('Form data:', data);
        // console.log('Image changes:', imageChanges);

        try {
            setIsSubmitting(true);

            const updatePayload = {
                id: complaintData.complaintId || complaintData.id,
                userId: complaintData.customerId,
                title: data.title,
                description: data.description,
                complaintCategory: Array.isArray(data.complaintType) ? data.complaintType[0] : [data.complaintType],
                complaintSubCategory: Array.isArray(data.complaintSubType) ? data.complaintSubType[0] : data.complaintSubType,
                customMetadata: JSON.stringify({
                    complaintRefNo: data.complaintReferenceNo,
                }),
                imagePath: uploadPath.COMPLAINTS.COMPLAINTS_UPLOAD,

                ...(imageChanges.removedImageUrls?.length > 0 && {
                    removedImageUrls: imageChanges.removedImageUrls
                }),
            };

            dispatch({
                type: "EDIT_COMPLAINT",
                payload: updatePayload
            });
            dispatch(clearImageChanges({ key: uploadPath.COMPLAINTS.COMPLAINTS_UPLOAD }));
            setIsEditMode(false);
            onClose();

        } catch (error) {
            console.error('Error updating complaint:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        setIsEditMode(false);
    };

    const handleUpdateComplaint = () => {
        const statusValue = watchedStatus;

        if (statusValue === complaintData.rawStatus.toLowerCase().replace(/[\s_]+/g, '')) {
            dispatch(showAlert({
                message: "No changes detected in the status. Please select a different status to update.",
                type: 'warning'
            }));
            return;
        }

        if (statusValue === 'resolved') {
            setShowResolvedDialog(true);
            setResolvedRemarks('');
            setResolvedError('');
            setRequestFeedback(true);
        } else {
            handleDirectStatusUpdate(statusValue);
        }
    };

    const handleDirectStatusUpdate = (statusValue) => {
        const formatStatus = (statusValue) => {
            const statusMap = {
                INPROGRESS: "IN_PROGRESS",
                OPEN: "OPEN",
                RESOLVED: "RESOLVED"
            };
            return statusMap[statusValue?.toUpperCase()];
        };

        const updatePayload = {
            id: complaintData?.complaintId || complaintData?.id,
            complaintSubCategory: complaintData?.subCategoryObject?.value || complaintData?.complaintSubCategory,
            status: formatStatus(statusValue),
            userId: complaintData.customerId,
        };

        dispatch({
            type: 'UPDATE_COMPLAINT_STATUS',
            payload: updatePayload
        });
    };

    const handleResolvedStatusUpdate = () => {

        if (resolvedRemarks.trim().length < 10) {
            setResolvedError('At least 10 characters are required');
            return;
        }
        setResolvedError('')
        const updatePayload = {
            id: complaintData?.complaintId || complaintData?.id,
            complaintSubCategory: complaintData?.subCategoryObject?.value || complaintData?.complaintSubCategory,
            status: "RESOLVED",
            customerFeedback: resolvedRemarks,
            feedbackRequest: requestFeedback,
            userId: complaintData.customerId,
        };

        dispatch({
            type: 'UPDATE_COMPLAINT_STATUS',
            payload: updatePayload
        });

        setShowResolvedDialog(false);
    };

    const tabs = [
        {
            label: 'Details',
            value: 'details',
            icon: FaEdit,
            panel: (
                <ComplaintDetailsWithImages
                    complaintData={complaintData}
                    isEditMode={isEditMode}
                    errors={errors}
                    control={control}
                    handleCancel={handleCancel}
                    onSubmit={onSubmit}
                    register={register}
                    handleSubmit={handleSubmit}
                    serviceRequestCategories={serviceRequestCategories}
                    serviceRequestSubcategories={serviceRequestSubcategories}
                    selectedComplaintType={selectedComplaintType}
                    isSubmitting={isSubmitting}
                    setValue={setValue}
                />
            )
        },
        {
            label: 'Feedback',
            value: 'feedback',
            icon: MdFeedback,
            panel: (
                <Box p={6}>
                    <Heading mb={4} size="lg" color={headingColor}>
                        Customer Feedback
                    </Heading>

                    {complaintData?.feedbackRating > 0 ? (
                        <Box
                            p={8}
                            border="1px solid"
                            borderColor="gray.200"
                            borderRadius="lg"
                            boxShadow="sm"
                            bg={cardBg}
                        >
                            <VStack spacing={6} align="stretch">
                                <Box>
                                    <Text fontSize="lg" fontWeight="semibold" color={textColor} mb={3}>
                                        Customer Rating
                                    </Text>
                                    <HStack spacing={2} align="center">
                                        <HStack spacing={1}>
                                            {[1, 2, 3, 4, 5].map((star) => (
                                                <Icon
                                                    key={star}
                                                    as={star <= parseInt(complaintData.feedbackRating) ? FaStar : LuStarOff}
                                                    color={star <= parseInt(complaintData.feedbackRating) ? "yellow.400" : "gray.300"}
                                                    boxSize={5}
                                                />
                                            ))}
                                        </HStack>
                                        <Text fontSize="lg" fontWeight="bold" color={textColor}>
                                            {complaintData.feedbackRating || 0}/5
                                        </Text>
                                    </HStack>
                                </Box>

                                {/* Customer Comments Section */}
                                {complaintData.customerFeedback && (
                                    <Box>
                                        <Text fontSize="lg" fontWeight="semibold" color={textColor} mb={3}>
                                            Customer Comments
                                        </Text>
                                        <Box
                                            p={4}
                                            bg="gray.50"
                                            borderRadius="md"
                                            border="1px solid"
                                            borderColor="gray.200"
                                        >
                                            <Text fontSize="md" color={textColor} lineHeight="1.6">
                                                {complaintData.customerFeedback}
                                            </Text>
                                        </Box>
                                    </Box>
                                )}

                                {/* Feedback Status */}
                                <HStack justify="space-between" pt={4}>
                                    <HStack spacing={3}>
                                        <Box
                                            w={3}
                                            h={3}
                                            borderRadius="full"
                                            bg="green.500"
                                        />
                                        <Text fontSize="sm" fontWeight="semibold" color="green.600">
                                            Feedback Successfully Collected
                                        </Text>
                                    </HStack>
                                    <Text fontSize="sm" color={mutedColor}>
                                        Submitted via Prodyt Mobile App
                                    </Text>
                                </HStack>

                                {/* Additional info box */}
                                <Box
                                    p={4}
                                    bg="green.50"
                                    borderRadius="md"
                                    border="1px solid"
                                    borderColor="green.200"
                                >
                                    <Text fontSize="sm" color="green.700">
                                        Customer feedback has been automatically collected through the mobile app notification system.
                                    </Text>
                                </Box>
                            </VStack>
                        </Box>
                    ) : (
                        <Box
                            p={12}
                            border="1px solid"
                            borderColor="gray.200"
                            borderRadius="lg"
                            boxShadow="sm"
                            bg={cardBg}
                            textAlign="center"
                        >
                            <VStack spacing={6}>
                                <Icon as={LuStar} boxSize={16} color="gray.300" />

                                <Heading size="md" color={textColor}>
                                    No Feedback Yet
                                </Heading>

                                {/* Supporting description */}
                                <Text fontSize="md" color={mutedColor} maxW="md" mx="auto">
                                    Customer feedback will appear here once they respond to the feedback
                                    request sent through the Prodyt mobile app.
                                </Text>
                            </VStack>
                        </Box>
                    )}
                </Box>
            )
        },
        {
            label: 'History',
            value: 'history',
            icon: MdHistory,
            panel: (
                <Box p={6}>
                    <Box
                        p={6}
                        border="1px solid"
                        borderColor="gray.200"
                        borderRadius="lg"
                        boxShadow="sm"
                        bg={cardBg}
                    >
                        <Flex align="center" justify="center" height="100%">
                            <VStack spacing={4}>
                                <Icon as={MdHistory} boxSize={12} color="teal.400" />
                                <Heading size="md" color={headingColor}>
                                    Activity History
                                </Heading>
                                <Text fontSize="md" color={mutedColor} textAlign="center" maxW="md">
                                    Track all activities, status changes, and updates
                                    made to this complaint over time.
                                </Text>
                                <Badge colorScheme="teal" variant="subtle" px={3} py={1}>
                                    Coming Soon
                                </Badge>
                            </VStack>
                        </Flex>
                    </Box>
                </Box>
            )
        },
        {
            label: 'View Product',
            value: 'product',
            icon: FaBox,
            panel: (
                <Box p={6}>
                    <VStack spacing={6} align="stretch">
                        {/* Product Header */}
                        <Box>
                            <HStack spacing={3} mb={4}>
                                <Icon as={FaBox} color="green.500" boxSize={6} />
                                <Heading size="lg" color={headingColor}>
                                    Product Details
                                </Heading>
                            </HStack>
                        </Box>

                        {/* Loading State */}
                        {complaintProductLoading && (
                            <Box p={8} border="1px solid" borderColor={borderColor} borderRadius="lg" textAlign="center" bg={cardBg}>
                                <VStack spacing={3}>
                                    <Box
                                        borderRadius="50%"
                                        border="4px solid"
                                        borderColor={borderColor}
                                        borderTopColor="green.500"
                                        boxSize="40px"
                                        animation="spin 1s linear infinite"
                                        css={{
                                            '@keyframes spin': {
                                                from: { transform: 'rotate(0deg)' },
                                                to: { transform: 'rotate(360deg)' },
                                            },
                                        }}
                                    />
                                    <Text color={mutedColor}>Loading product details...</Text>
                                </VStack>
                            </Box>
                        )}

                        {/* Error State */}
                        {complaintProductError && !complaintProductLoading && (
                            <Box p={8} border="1px solid" borderColor="red.200" borderRadius="lg" textAlign="center" bg="red.50">
                                <VStack spacing={3}>
                                    <Icon as={FaBox} boxSize={12} color="red.400" />
                                    <Heading size="sm" color="red.600">
                                        Error Loading Product
                                    </Heading>
                                    <Text fontSize="sm" color="red.500">
                                        {complaintProductError}
                                    </Text>
                                </VStack>
                            </Box>
                        )}

                        {/* Product Information Grid */}
                        {complaintProductData && !complaintProductLoading && (
                            <>
                                <Box p={6} borderRadius="lg" bg={productDetailsBg} border="1px solid" borderColor={borderColor}>
                                    <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6}>
                                        <Box>
                                            <Text fontSize="sm" fontWeight="medium" color={mutedColor} mb={1}>
                                                Product Name
                                            </Text>
                                            <Text fontSize="md" fontWeight="semibold" color={textColor}>
                                                {complaintProductData?.name || 'Not Available'}
                                            </Text>
                                        </Box>

                                        <Box>
                                            <Text fontSize="sm" fontWeight="medium" color={mutedColor} mb={1}>
                                                Category
                                            </Text>
                                            <Text fontSize="md" fontWeight="semibold" color={textColor}>
                                                {complaintProductData?.categoryName || complaintProductData?.category || 'Not Available'}
                                            </Text>
                                        </Box>

                                        <Box>
                                            <Text fontSize="sm" fontWeight="medium" color={mutedColor} mb={1}>
                                                Sub Category
                                            </Text>
                                            <Text fontSize="md" fontWeight="semibold" color={textColor}>
                                                {complaintProductData?.subCategoryName || complaintProductData?.subCategory || 'Not Available'}
                                            </Text>
                                        </Box>

                                        <Box>
                                            <Text fontSize="sm" fontWeight="medium" color={mutedColor} mb={1}>
                                                Warranty Duration
                                            </Text>
                                            <Text fontSize="md" fontWeight="semibold" color={textColor}>
                                                {complaintProductData?.warrantyDurationInDays ? `${complaintProductData.warrantyDurationInDays} Days` : 'Not Available'}
                                            </Text>
                                        </Box>

                                        <Box>
                                            <Text fontSize="sm" fontWeight="medium" color={mutedColor} mb={1}>
                                                Warranty Status
                                            </Text>
                                            <Badge colorScheme="blue" variant="subtle" px={3} py={1}>
                                                {complaintProductData?.warrantyStatus || 'Unknown'}
                                            </Badge>
                                        </Box>

                                        <Box>
                                            <Text fontSize="sm" fontWeight="medium" color={mutedColor} mb={1}>
                                                Extended Warranty Duration
                                            </Text>
                                            <Text fontSize="md" fontWeight="semibold" color={textColor}>
                                                {complaintProductData?.extWarrantyDurationInDays ? `${complaintProductData.extWarrantyDurationInDays} Days` : 'Not Available'}
                                            </Text>
                                        </Box>
                                    </Grid>
                                </Box>

                                {/* Product Images */}
                                {complaintProductData?.images && complaintProductData?.images.length > 0 && (
                                    <Box p={6} borderRadius="lg" bg={productImagesBg} border="1px solid" borderColor={borderColor}>
                                        <Heading size="md" color={headingColor} mb={4}>
                                            Product Images
                                        </Heading>
                                        <SimpleGrid columns={{ base: 2, md: 3, lg: 4 }} spacing={4} w="full">
                                            {complaintProductData?.images.map((image, index) => (
                                                <Box
                                                    key={index}
                                                    position="relative"
                                                    borderRadius="lg"
                                                    overflow="hidden"
                                                    border="2px solid"
                                                    borderColor={borderColor}
                                                    bg={cardBg}
                                                    _hover={{
                                                        borderColor: "green.400",
                                                        transform: "translateY(-4px)",
                                                        boxShadow: "0 8px 25px rgba(72, 187, 120, 0.15)"
                                                    }}
                                                    transition="all 0.3s ease"
                                                    cursor="pointer"
                                                    onClick={() => {
                                                        setSelectedProductImage(image);
                                                        setIsProductImageDialogOpen(true);
                                                    }}
                                                >
                                                    <Image
                                                        src={image.url || image}
                                                        alt={image.name || `Product image ${index + 1}`}
                                                        width="100%"
                                                        height="120px"
                                                        objectFit="cover"
                                                    />
                                                    <Box
                                                        position="absolute"
                                                        bottom={0}
                                                        left={0}
                                                        right={0}
                                                        bg="linear-gradient(transparent, rgba(0,0,0,0.7))"
                                                        p={2}
                                                    >
                                                        <Text color="white" fontSize="xs" fontWeight="500" isTruncated>
                                                            {image.name || `Image ${index + 1}`}
                                                        </Text>
                                                    </Box>
                                                </Box>
                                            ))}
                                        </SimpleGrid>
                                    </Box>
                                )}

                                {/* No Images Message */}
                                {(!complaintProductData?.images || complaintProductData?.images.length === 0) && (
                                    <Box p={8} border="1px solid" borderColor={borderColor} borderRadius="lg" textAlign="center" bg={productImagesBg}>
                                        <VStack spacing={3}>
                                            <Icon as={FaBox} boxSize={12} color="gray.300" />
                                            <Heading size="sm" color={textColor}>
                                                No Images Available
                                            </Heading>
                                            <Text fontSize="sm" color={mutedColor}>
                                                No product images have been registered for this product.
                                            </Text>
                                        </VStack>
                                    </Box>
                                )}
                            </>
                        )}

                        {/* Empty State */}
                        {!complaintProductData && !complaintProductLoading && !complaintProductError && (
                            <Box p={8} border="1px solid" borderColor={borderColor} borderRadius="lg" textAlign="center" bg={cardBg}>
                                <VStack spacing={3}>
                                    <Icon as={FaBox} boxSize={12} color="gray.300" />
                                    <Heading size="sm" color={textColor}>
                                        No Product Data
                                    </Heading>
                                    <Text fontSize="sm" color={mutedColor}>
                                        Product details could not be loaded. Please try again.
                                    </Text>
                                </VStack>
                            </Box>
                        )}
                    </VStack>
                </Box>
            )
        }
    ];

    const alreadyResolved = complaintData?.rawStatus?.toLowerCase().replace(/[\s_]+/g, '') === "resolved";

    return (
        <>
            <DialogRoot open={isOpen} onOpenChange={({ open }) => !open && (onClose(), setIsEditMode(false))}>
                <DialogContent position="relative" maxWidth='6xl' height="90vh" bg={bgColor} placement="center" mt="120px">
                    {/* Enhanced Dialog Header */}
                    <DialogHeader borderBottom="1px solid" borderColor={borderColor} pb={4}>
                        <Flex justify="space-between" align="flex-start" width="100%">
                            <VStack align="start" spacing={2}>
                                <DialogTitle fontSize="md" color={headingColor}>
                                    <Text fontWeight="bold">Complaint ID - {complaintData?.complaintId}</Text>
                                    <Text fontWeight="medium">Product Name - {complaintData?.productName}</Text>
                                </DialogTitle>

                                <HStack spacing={4} flexWrap="wrap">
                                    {/* Enhanced Status Badge */}
                                    <Badge
                                        colorPalette="blue"
                                        variant="solid"
                                        px={2}
                                        py={1}
                                        borderRadius="xl"
                                        fontSize="xs"
                                        fontWeight="medium"
                                    >
                                        {complaintData?.rawStatus}
                                    </Badge>

                                    <Text fontSize="sm" color={mutedColor}>
                                        Created on {complaintData?.createdDate}
                                    </Text>

                                    <Text fontSize="sm" color={mutedColor}>
                                        Assigned to {complaintData?.assignedTo || 'Not Provided'}
                                    </Text>
                                </HStack>
                            </VStack>

                            <HStack spacing={3}>
                                {/* Enhanced Edit/View Mode Button */}
                                {activeTab === 'details' &&
                                    < ButtonBox
                                        icon={<FaEdit />}
                                        size="sm"
                                        name={isEditMode ? 'View Mode' : 'Edit Details'}
                                        variant={isEditMode ? 'solid' : 'outline'}
                                        colorPalette={isEditMode ? 'orange' : 'blue'}
                                        onClick={() => setIsEditMode(!isEditMode)}
                                        disabled={isSubmitting || alreadyResolved}
                                    />
                                }
                                <DialogCloseTrigger insetEnd='0' top='0' />
                            </HStack>

                        </Flex>
                    </DialogHeader>

                    <DialogBody p={0} overflow="auto">
                        <Grid templateColumns={{ base: '1fr', lg: '1fr 320px' }} gap={0} minHeight="400px">
                            {/* Main Content */}
                            <Box>
                                <FitTabs
                                    value={activeTab}
                                    onChange={handleTabChange}
                                    tabs={tabs}
                                    variant="enclosed"
                                />
                            </Box>

                            {/* Enhanced Actions Sidebar */}
                            <Box
                                borderLeft="1px solid"
                                borderColor={borderColor}
                                bg={sidebarBg}
                                p={4}
                            >
                                <VStack spacing={4} align="stretch">
                                    {/* Enhanced Quick Actions */}
                                    <Box>
                                        <Text fontSize="sm" fontWeight="medium" mb={3} color={mutedColor}>
                                            Actions
                                        </Text>
                                        <Text fontSize="sm" fontWeight="bold" mb={2} color="gray.600">
                                            Status
                                        </Text>
                                        <SelectBox
                                            label=""
                                            control={control}
                                            name="status"
                                            items={statusOptions}
                                            disabled={alreadyResolved}
                                        />
                                    </Box>
                                    <Box>
                                        <Text fontSize="sm" fontWeight="medium" mb={3} color={mutedColor}>
                                            Quick Actions
                                        </Text>
                                        <VStack spacing={2} align="stretch">
                                            <ButtonBox
                                                size="sm"
                                                name="Send Email"
                                                icon={<FaEnvelope />}
                                                variant="outline"
                                                borderRadius="md"
                                                colorPalette="green"
                                                disabled={alreadyResolved}
                                                onClick={() => {
                                                    setShowEmailDialog(true);
                                                }}
                                            />

                                            {showWhatsappFeature &&
                                                <ButtonBox
                                                    size="sm"
                                                    name="Send WhatsApp Message"
                                                    icon={<FaWhatsapp />}
                                                    variant="outline"
                                                    borderRadius="md"
                                                    colorPalette="green"
                                                    disabled={alreadyResolved}
                                                    onClick={() => {
                                                        setIsWhatsappOpen(true);
                                                    }}
                                                />
                                            }

                                            <ButtonBox
                                                name="Add Note"
                                                icon={<MdNoteAdd />}
                                                size="sm"
                                                variant="outline"
                                                borderColor="gray.300"
                                                colorPalette="orange"
                                                disabled={alreadyResolved}
                                                onClick={() => setShowNoteDialog(true)}
                                            />
                                        </VStack>
                                    </Box>

                                    {/* Enhanced Mark as Resolved Button */}
                                    <ButtonBox
                                        icon={alreadyResolved && <FaCheckCircle />}
                                        name={alreadyResolved ? "Complaint Resolved" : "Update Complaint"}
                                        size="sm"
                                        variant="solid"
                                        colorPalette="green"
                                        width="100%"
                                        disabled={alreadyResolved}
                                        onClick={handleUpdateComplaint}
                                    />
                                </VStack>
                            </Box>
                        </Grid>
                    </DialogBody>
                </DialogContent>
            </DialogRoot>

            {/* Resolved Status Dialog */}
            <DialogRoot open={showResolvedDialog} onOpenChange={({ open }) => !open && setShowResolvedDialog(false)}>
                <DialogContent
                    maxWidth="md"
                    bg={bgColor}
                    borderRadius="lg"
                    boxShadow="xl"
                    placement='center'
                    p={6}
                >
                    {/* Header */}
                    <Flex justify="space-between" align="start" mb={4}>
                        <HStack spacing={3}>
                            <Icon as={FaEdit} color="blue.500" boxSize={5} />
                            <Text fontSize="lg" fontWeight="bold" color={textColor} lineHeight="short">
                                Resolve Complaint - {complaintData?.complaintId}
                            </Text>
                        </HStack>
                        <DialogCloseTrigger />
                    </Flex>

                    {/* Body */}
                    <VStack spacing={5} align="stretch">
                        {/* Remarks */}
                        <Box>
                            <Text fontSize="sm" fontWeight="medium" mb={2} color="gray.600">
                                Resolved Status Remarks
                            </Text>
                            <Textarea
                                placeholder="Type here..."
                                value={resolvedRemarks}
                                onChange={(e) => {
                                    setResolvedRemarks(e.target.value);
                                    if (resolvedError && e.target.value.trim()) {
                                        setResolvedError('');
                                    }
                                }}

                                minHeight="100px"
                                borderRadius="md"
                                border="1px solid"
                                borderColor="gray.300"
                                _focus={{
                                    borderColor: "blue.400",
                                    boxShadow: "0 0 0 1px blue.400",
                                }}
                                resize="vertical"
                            />
                            {resolvedError && (
                                <Text fontSize="sm" color="red.500" mt={1}>
                                    {resolvedError}
                                </Text>
                            )}
                        </Box>

                        {/* Request Feedback */}
                        <Box>
                            <CheckboxChakra
                                name="requestFeedback"
                                label={
                                    <Text fontSize="sm" fontWeight="medium" color={textColor}>
                                        Request Feedback
                                    </Text>
                                }
                                checked={requestFeedback}
                                onCheckedChange={(val) => {
                                    setRequestFeedback(val.checked)
                                }}
                                colorPalette="blue"
                                size="md"
                                variant="outline"
                                mt={2}
                            />

                            {/* Notification Box */}
                            <Box
                                mt={3}
                                p={3}
                                bg={lightBgColor}
                                borderRadius="md"
                                border="1px solid"
                                borderColor="blue.200"
                            >
                                <HStack spacing={2} mb={1}>
                                    <Icon as={FaEnvelope} color="blue.500" boxSize={4} />
                                    <Text fontSize="sm" fontWeight="semibold" color="blue.600">
                                        Mobile Notification
                                    </Text>
                                </HStack>
                                <Text fontSize="xs" color="blue.600" lineHeight="short">
                                    Customer will receive a push notification in the Prodyt mobile app to provide feedback.
                                </Text>
                            </Box>
                        </Box>
                    </VStack>

                    {/* Footer */}
                    <Flex mt={8} pt={4} borderTop="1px solid" borderColor={borderColor} justify="flex-end">
                        <HStack spacing={3} justify="flex-end">
                            <ButtonBox
                                name="Cancel"
                                variant="outline"
                                onClick={() => {
                                    setResolvedRemarks('')
                                    setShowResolvedDialog(false)
                                }}
                                px={6}
                                borderColor="gray.300"
                            />
                            <ButtonBox
                                name="Resolve"
                                colorPalette="green"
                                variant="solid"
                                onClick={handleResolvedStatusUpdate}
                                px={6}
                            />
                        </HStack>
                    </Flex>
                </DialogContent>
            </DialogRoot>

            {/* Note Dialog */}
            <NoteDialog
                isOpen={showNoteDialog}
                onClose={() => setShowNoteDialog(false)}
            />

            {/* Product Image Zoom Dialog */}
            <ImagePreviewDialog
                isOpen={isProductImageDialogOpen}
                onOpenChange={(details) => {
                    setIsProductImageDialogOpen(details.open);
                    if (!details.open) {
                        setSelectedProductImage(null);
                    }
                }}
                selectedImage={selectedProductImage}
                title={selectedProductImage?.name}
                defaultTitle="Product Image"
                width="5xl"
                height="auto"
                bgColor={bgColor}
                borderColor={borderColor}
            />

            {/* Email Dialog */}
            <SendEmailDialog
                isOpen={showEmailDialog}
                onClose={() => setShowEmailDialog(false)}
                recipientEmail={complaintData?.email}
                complaintId={complaintData?.complaintId}
                complaintData={complaintData}
            />

            {/* WhatsApp Dialog */}
            <SendWhatsappDialog
                isOpen={isWhatsappOpen}
                onClose={() => setIsWhatsappOpen(false)}
                recipientPhone={complaintData?.contactNumber}
                complaintId={complaintData?.complaintId}
                complaintData={complaintData}
                useCase="CUSTOMER_COMPLAINT"
                useCaseId="customer_complaint"
            />
        </>
    );
};

export default ComplaintDetailsDialog;
